import { useCallback, useEffect, useState } from 'react';
import type { PublicUser, UserHandover, UserRole } from '@bazaar/shared';
import { api, ApiError } from '../../lib/api.js';
import { useAuth } from '../../context/AuthContext.js';
import { useToast } from '../../context/ToastContext.js';
import { BigButton } from '../../components/BigButton.js';
import { Spinner } from '../../components/Spinner.js';
import { EmptyState } from '../../components/EmptyState.js';
import { Page, PageHeader, Field } from '../../components/layout.js';
import { HandoverDialog } from '../../components/admin/HandoverDialog.js';

function formatHandoverDate(iso: string): string {
  return new Intl.DateTimeFormat('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Asia/Kuala_Lumpur',
  }).format(new Date(iso));
}

export function UsersPage() {
  const { user: me } = useAuth();
  const { showToast } = useToast();
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordVisible, setNewPasswordVisible] = useState(false);
  const [newRole, setNewRole] = useState<UserRole>('sales');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [editRole, setEditRole] = useState<UserRole>('sales');
  const [editActive, setEditActive] = useState(true);
  const [editPassword, setEditPassword] = useState('');
  const [editPasswordVisible, setEditPasswordVisible] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [handoverUser, setHandoverUser] = useState<PublicUser | null>(null);

  const [historyOpenId, setHistoryOpenId] = useState<number | null>(null);
  const [history, setHistory] = useState<UserHandover[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { users } = await api.listUsers();
      setUsers(users);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate() {
    setCreating(true);
    setCreateError(null);
    try {
      await api.createUser({
        username: newUsername.trim(),
        displayName: newDisplayName.trim(),
        password: newPassword,
        role: newRole,
      });
      setNewUsername('');
      setNewDisplayName('');
      setNewPassword('');
      setNewRole('sales');
      setShowAddForm(false);
      await load();
      showToast('Staff account created', 'success');
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Could not create the account');
    } finally {
      setCreating(false);
    }
  }

  function startEdit(u: PublicUser) {
    setEditingId(u.id);
    setEditDisplayName(u.displayName);
    setEditUsername(u.username);
    setEditRole(u.role);
    setEditActive(u.isActive);
    setEditPassword('');
    setEditError(null);
  }

  async function handleSaveEdit(id: number, isSelf: boolean, currentUsername: string) {
    setSavingEdit(true);
    setEditError(null);
    try {
      const usernameTrimmed = editUsername.trim();
      await api.updateUser(id, {
        displayName: editDisplayName.trim(),
        // Only sent when it actually changed - the server also signs the
        // account out when this changes, so there's no point tripping that
        // on a no-op save.
        ...(usernameTrimmed.toLowerCase() !== currentUsername.toLowerCase() ? { username: usernameTrimmed } : {}),
        // An admin can never change their own role or active status (the
        // API rejects it with CANNOT_MODIFY_SELF regardless) - omit both
        // fields entirely for a self-edit so a plain display-name change,
        // or a self password reset, isn't blocked by that guard too.
        ...(isSelf ? {} : { role: editRole, isActive: editActive }),
        ...(editPassword ? { password: editPassword } : {}),
      });
      setEditingId(null);
      await load();
      showToast('User updated', 'success');
    } catch (err) {
      setEditError(err instanceof ApiError ? err.message : 'Could not update the user');
    } finally {
      setSavingEdit(false);
    }
  }

  async function loadHistory(id: number) {
    setHistoryLoading(true);
    try {
      const res = await api.listUserHandovers(id);
      setHistory(res.handovers);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not load handover history', 'error');
      setHistoryOpenId(null);
    } finally {
      setHistoryLoading(false);
    }
  }

  function toggleHistory(id: number) {
    if (historyOpenId === id) {
      setHistoryOpenId(null);
      return;
    }
    setHistoryOpenId(id);
    void loadHistory(id);
  }

  async function handleHandedOver(updated: PublicUser) {
    setHandoverUser(null);
    await load();
    showToast(`Handed over to ${updated.displayName}`, 'success');
    if (historyOpenId === updated.id) {
      void loadHistory(updated.id);
    }
  }

  if (loading) return <Spinner label="Loading users" />;
  if (error) return <EmptyState title="Could not load users" description={error} />;

  return (
    <Page>
      <PageHeader
        title="Users"
        actions={<BigButton onClick={() => setShowAddForm((v) => !v)}>{showAddForm ? 'Close' : 'Add staff'}</BigButton>}
      />

      {showAddForm && (
        <div className="card form-stack form-width">
          <Field label="Username" htmlFor="new-username">
            <input
              id="new-username"
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Display name" htmlFor="new-display-name">
            <input
              id="new-display-name"
              type="text"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Password (min 8 characters)" htmlFor="new-password">
            <div className="flex gap-2">
              <input
                id="new-password"
                type={newPasswordVisible ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input flex-1"
              />
              <button
                type="button"
                onClick={() => setNewPasswordVisible((v) => !v)}
                className="min-h-12 px-3 rounded-lg font-body font-semibold text-base text-teal-dark border-2 border-border"
              >
                {newPasswordVisible ? 'Hide' : 'Show'}
              </button>
            </div>
          </Field>
          <Field label="Role" htmlFor="new-role">
            <select
              id="new-role"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as UserRole)}
              className="input"
            >
              <option value="sales">Sales</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          {createError && (
            <p role="alert" className="font-body text-sm text-danger">
              {createError}
            </p>
          )}
          <BigButton
            disabled={creating || !newUsername.trim() || !newDisplayName.trim() || newPassword.length < 8}
            onClick={() => void handleCreate()}
          >
            {creating ? 'Creating…' : 'Create account'}
          </BigButton>
        </div>
      )}

      <ul className="list-stack">
        {users.map((u) => (
          <li
            key={u.id}
            data-testid={u.id === me?.id ? 'user-row-self' : `user-row-${u.id}`}
            className="card-row flex flex-col gap-2"
          >
            {editingId === u.id ? (
              <div className="form-stack form-width">
                <Field label="Display name" htmlFor={`edit-name-${u.id}`}>
                  <input
                    id={`edit-name-${u.id}`}
                    type="text"
                    value={editDisplayName}
                    onChange={(e) => setEditDisplayName(e.target.value)}
                    className="input"
                  />
                </Field>
                <Field label="Login username" htmlFor={`edit-username-${u.id}`}>
                  <input
                    id={`edit-username-${u.id}`}
                    type="text"
                    value={editUsername}
                    onChange={(e) => setEditUsername(e.target.value)}
                    className="input"
                  />
                </Field>
                <p className="font-body text-sm text-muted -mt-2">
                  Changing the login username signs this person out everywhere - changing just the display name does
                  not.
                </p>
                {u.id === me?.id ? (
                  <p className="font-body text-sm text-muted">
                    You can&apos;t change your own role or active status here - ask another admin.
                  </p>
                ) : (
                  <>
                    <Field label="Role" htmlFor={`edit-role-${u.id}`}>
                      <select
                        id={`edit-role-${u.id}`}
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value as UserRole)}
                        className="input"
                      >
                        <option value="sales">Sales</option>
                        <option value="admin">Admin</option>
                      </select>
                    </Field>
                    <label className="flex items-center gap-3 font-body font-semibold text-base text-text min-h-12">
                      <input
                        type="checkbox"
                        checked={editActive}
                        onChange={(e) => setEditActive(e.target.checked)}
                        className="h-6 w-6 min-h-0 accent-teal shrink-0"
                      />
                      Active
                    </label>
                  </>
                )}
                <Field label="Reset password (optional)" htmlFor={`edit-password-${u.id}`}>
                  <div className="flex gap-2">
                    <input
                      id={`edit-password-${u.id}`}
                      type={editPasswordVisible ? 'text' : 'password'}
                      value={editPassword}
                      onChange={(e) => setEditPassword(e.target.value)}
                      placeholder="Leave blank to keep current password"
                      className="input flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => setEditPasswordVisible((v) => !v)}
                      className="min-h-12 px-3 rounded-lg font-body font-semibold text-base text-teal-dark border-2 border-border"
                    >
                      {editPasswordVisible ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </Field>
                {editError && (
                  <p role="alert" className="font-body text-sm text-danger">
                    {editError}
                  </p>
                )}
                <div className="flex gap-3">
                  <BigButton variant="outline" onClick={() => setEditingId(null)}>
                    Cancel
                  </BigButton>
                  <BigButton
                    disabled={
                      savingEdit ||
                      (editPassword.length > 0 && editPassword.length < 8) ||
                      !editDisplayName.trim() ||
                      !editUsername.trim()
                    }
                    onClick={() => void handleSaveEdit(u.id, u.id === me?.id, u.username)}
                  >
                    {savingEdit ? 'Saving…' : 'Save'}
                  </BigButton>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-body font-semibold text-base text-text truncate">
                      {u.displayName} {u.id === me?.id && <span className="text-muted font-normal">(you)</span>}
                    </p>
                    <p className="font-body text-sm text-muted">
                      @{u.username} &middot; <span className="font-semibold">{u.shortCode}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="rounded-full bg-aqua text-teal-dark font-body font-bold text-sm px-2 py-1 capitalize">
                      {u.role}
                    </span>
                    {!u.isActive && (
                      <span className="rounded-full bg-gray-200 text-muted font-body font-bold text-sm px-2 py-1">
                        Inactive
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => startEdit(u)}
                      className="min-h-12 px-3 rounded-lg font-heading font-semibold text-base text-teal-dark"
                    >
                      Edit
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {u.id !== me?.id && (
                    <button
                      type="button"
                      onClick={() => setHandoverUser(u)}
                      className="min-h-12 px-3 rounded-lg font-heading font-semibold text-base text-teal-dark border-2 border-border"
                    >
                      Hand over to new person
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleHistory(u.id)}
                    className="min-h-12 px-3 rounded-lg font-body font-semibold text-sm text-muted"
                  >
                    {historyOpenId === u.id ? 'Hide history' : 'Handover history'}
                  </button>
                </div>
                {historyOpenId === u.id && (
                  <div className="card-row bg-aqua/30">
                    {historyLoading ? (
                      <Spinner label="Loading handover history" />
                    ) : history.length === 0 ? (
                      <p className="font-body text-sm text-muted">No handovers yet for this account.</p>
                    ) : (
                      <ul className="flex flex-col gap-2">
                        {history.map((h) => (
                          <li key={h.id} className="font-body text-sm text-text">
                            <span className="text-muted">{formatHandoverDate(h.at)}</span> &middot; {h.fromDisplayName}{' '}
                            (@{h.fromUsername}) &rarr; {h.toDisplayName} (@{h.toUsername})
                            {h.note && <span className="text-muted"> &mdash; {h.note}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </>
            )}
          </li>
        ))}
      </ul>

      {handoverUser && (
        <HandoverDialog
          user={handoverUser}
          onClose={() => setHandoverUser(null)}
          onHandedOver={(updated) => void handleHandedOver(updated)}
        />
      )}
    </Page>
  );
}
